<script setup lang="ts">
import { cn } from '@/utils';
import type { HTMLAttributes } from 'vue';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps<{
    defaultValue?: string | number;
    /** `null` renders as an empty field, for the nullable strings optional form fields carry. */
    modelValue?: string | number | null;
    class?: HTMLAttributes['class'];
    rows?: number;
    maxRows?: number;
    maxlength?: number;
    error?: string | boolean;
}>();

const emits = defineEmits<{
    (e: 'update:modelValue', payload: string | number): void;
}>();

/**
 * The character counter forced a wrapping `<div>` around the control, and Vue
 * falls attributes through to the ROOT element — so `id`, `placeholder`, `name`,
 * `required` and every `aria-*` were landing on that div and doing nothing. The
 * visible symptom is a `<Label for="…">` that points at a div: clicking the
 * label does not focus the box. Bound explicitly on the `<textarea>` below.
 *
 * `class` is unaffected either way — it is a declared prop, so it never reaches
 * `$attrs` and is still merged through `cn()`.
 */
defineOptions({ inheritAttrs: false });

const textareaRef = ref<HTMLTextAreaElement>();
const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: props.defaultValue,
});

const charCount = computed(() => String(modelValue.value ?? '').length);
const isNearLimit = computed(() => props.maxlength && charCount.value >= props.maxlength * 0.9);

const autoResize = () => {
    const textarea = textareaRef.value;
    if (!textarea) return;

    textarea.style.height = 'auto';

    const minRows = props.rows || 1;
    const maxRows = props.maxRows || 4;

    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseInt(computedStyle.lineHeight) || 20;
    const paddingY = parseInt(computedStyle.paddingTop) + parseInt(computedStyle.paddingBottom);

    const minHeight = lineHeight * minRows + paddingY;
    const maxHeight = lineHeight * maxRows + paddingY;

    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
};

watch(modelValue, () => autoResize(), { flush: 'post' });

onMounted(() => {
    nextTick(() => autoResize());
});
</script>

<template>
    <div>
        <textarea
            ref="textareaRef"
            v-bind="$attrs"
            v-model="modelValue"
            :rows="rows || 1"
            :maxlength="maxlength"
            :aria-invalid="error ? true : undefined"
            :class="
                cn(
                    'flex min-h-[80px] w-full rounded-sm border border-input bg-input-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none',
                    error && 'border-destructive focus-visible:ring-destructive',
                    props.class,
                )
            "
            @input="autoResize"
        />
        <p
            v-if="maxlength"
            class="mt-1 text-right text-xs tabular-nums"
            :class="isNearLimit ? 'text-destructive' : 'text-muted-foreground'"
        >
            {{ charCount }}/{{ maxlength }}
        </p>
    </div>
</template>
